import { Autocomplete, TextField } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { t } from 'i18next';
import { SyntheticEvent, memo, useCallback, useState } from 'react';
import { UserRole, getUserRoleLabel } from '../../organization/users/enums/UserRole';

type Props = {
  label: string;
  focusedLabel: string;
  disabledReason: string | null;
  values: UserRole[];
  onChange: (roles: UserRole[]) => void;
};

const ALL_ROLES = [UserRole.VIEWER, UserRole.UPLOADER, UserRole.ANALYST, UserRole.AUTH_ADMIN, UserRole.ADMIN];

export const RolesAutocomplete = memo(({ label, focusedLabel, disabledReason, values, onChange }: Props) => {
  const [focused, setFocused] = useState(false);
  const [dirty, setDirty] = useState(false);

  const onFocus = useCallback(() => setFocused(true), []);
  const onBlur = useCallback(() => setFocused(false), []);
  const handleChange = useCallback(
    (_: SyntheticEvent, values: UserRole[]) => {
      onChange(values);
      setDirty(true);
    },
    [onChange]
  );

  const valid = !dirty || values.length > 0;
  const error = valid ? null : t('validators.existential.required');
  const helperText = error ?? disabledReason;
  const displayLabel = focused || values.length > 0 ? focusedLabel : label;
  const disabled = disabledReason !== null;

  return (
    <Autocomplete
      multiple
      options={ALL_ROLES}
      getOptionLabel={getUserRoleLabel}
      value={values}
      onChange={handleChange}
      disabled={disabled}
      disableCloseOnSelect={true}
      sx={{ '& .MuiChip-root.Mui-disabled': { opacity: 0.8, '& .MuiChip-deleteIcon': { display: 'none' } } }}
      renderInput={(params) => (
        <TextField
          {...deepmerge(params, {
            inputProps: { 'aria-controls': params.inputProps['aria-controls'] ?? '', 'aria-required': !disabled },
            InputLabelProps: { required: !disabled },
          })}
          variant="standard"
          error={Boolean(error)}
          helperText={helperText}
          label={displayLabel}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      )}
    />
  );
});
